// src/api/mock/recepcionDigital.js
//
// ── Design decisions ──────────────────────────────────────────────────────────
// Mock API layer for Recepción Digital. The UI is written against these async
// functions; when the real backend lands (encomienda_intake_drafts + wa-notify /
// wa-intake Edge Functions), this module is swapped for a real client with the
// SAME signatures — components never import fixtures directly.
//
// - Module-level state seeded from makeFixtures(); resetMockData() reseeds it
//   so tests are hermetic.
// - Every function returns DEEP COPIES (JSON round-trip — the data is plain
//   JSON) so components can't mutate the "server" state by accident.
// - Realistic latency: each call awaits a random delay in
//   [_mockConfig.minDelay, _mockConfig.maxDelay] (200–800 ms by default).
//   Tests set both to 0 for determinism.
// - Deterministic error hook: set _mockConfig.failNext = '<fnName>' and the
//   NEXT call to that function rejects once with a Spanish connection error —
//   this is how every loading/error/retry surface is exercised without a
//   network.
// - All thrown messages are user-facing Spanish (the UI may surface them).

import { makeFixtures } from './recepcionDigital.fixtures';

// ── Config + state ────────────────────────────────────────────────────────────

export const _mockConfig = {
  minDelay: 200,
  maxDelay: 800,
  failNext: null,   // e.g. 'listDrafts' → next listDrafts() call rejects once
};

let drafts = makeFixtures();
let agentMsgCounter = 0;

export function resetMockData() {
  drafts = makeFixtures();
  agentMsgCounter = 0;
  _mockConfig.failNext = null;
}

// ── Internals ─────────────────────────────────────────────────────────────────

const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));

async function simulateNetwork(fnName) {
  const { minDelay, maxDelay, failNext } = _mockConfig;
  const span = Math.max(0, maxDelay - minDelay);
  await new Promise((resolve) => setTimeout(resolve, minDelay + Math.random() * span));
  if (failNext === fnName) {
    _mockConfig.failNext = null;
    throw new Error('Error de conexión simulado. Intente de nuevo.');
  }
}

function findDraftOrThrow(id) {
  const draft = drafts.find((d) => d.id === id);
  if (!draft) throw new Error('Encomienda no encontrada');
  return draft;
}

// ── API ───────────────────────────────────────────────────────────────────────

export async function listDrafts() {
  await simulateNetwork('listDrafts');
  return deepCopy(drafts);
}

export async function getDraft(id) {
  await simulateNetwork('getDraft');
  return deepCopy(findDraftOrThrow(id));
}

// Shallow-merges top-level keys. `packages` is replaced wholesale when present —
// the UI always sends the full packages array (autosave sends complete state).
export async function updateDraft(id, patch) {
  await simulateNetwork('updateDraft');
  const draft = findDraftOrThrow(id);
  Object.assign(draft, patch);
  draft.updated_at = new Date().toISOString();
  return deepCopy(draft);
}

// Appends the pre-drafted operator question to the WhatsApp thread as an agent
// message and hands the ball back to the client (status → esperando_cliente).
export async function sendClientQuestion(id, { fieldRef, message }) {
  await simulateNetwork('sendClientQuestion');
  const draft = findDraftOrThrow(id);
  agentMsgCounter += 1;
  draft.whatsapp_thread.push({
    id: `wa-agent-${Date.now()}-${agentMsgCounter}`,
    from: 'agent',
    text: message,
    field_ref: fieldRef,
    timestamp: new Date().toISOString(),
  });
  draft.status = 'esperando_cliente';
  draft.updated_at = new Date().toISOString();
  return deepCopy(draft);
}

// Terminal action: the Anexo is produced and the card leaves the queue.
export async function generateAnexo(id) {
  await simulateNetwork('generateAnexo');
  findDraftOrThrow(id);
  drafts = drafts.filter((d) => d.id !== id);
  return {
    ok: true,
    filename: 'Anexo_' + id + '_' + new Date().toISOString().slice(0, 10) + '.pdf',
  };
}

// Terminal action: the draft is discarded and leaves the queue.
export async function discardDraft(id) {
  await simulateNetwork('discardDraft');
  findDraftOrThrow(id);
  drafts = drafts.filter((d) => d.id !== id);
  return { ok: true };
}

// Non-terminal: hides the card from the operator's priority view for 2 hours.
export async function snoozeDraft(id) {
  await simulateNetwork('snoozeDraft');
  const draft = findDraftOrThrow(id);
  draft.snoozed_until = new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString();
  draft.updated_at = new Date().toISOString();
  return deepCopy(draft);
}
